import type { FC, ReactNode } from 'react'
import type { ProfileRow } from '@/constants/content'

import Image from 'next/image'
import { useRef, useState } from 'react'
import { ArrowUpRight, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import SectionHeading from '@/components/landing/section-heading'
import Sparkle from '@/components/landing/decor/sparkle'
import MahoniaSprig from '@/components/landing/decor/mahonia'
import ObservationHud from '@/components/landing/decor/observation-hud'
import WorldMap from '@/components/landing/decor/world-map'
import PatternArt from '@/components/landing/decor/pattern-art'
import GeoGround from '@/components/landing/decor/geo-ground'

import {
    fromToEach,
    gsap,
    MOTION_QUERIES,
    primeOnRefresh,
    useGSAP
} from '@/lib/motion'

import {
    MAHO_COPY,
    NIA_VARIANTS,
    PROFILE_COPY,
    RECORD_PHOTOS,
    STORY_COPY,
    WORLD_COPY
} from '@/constants/content'

// Where each chapter sits in the pinned scene (0 to 1). Desktop navigation lands on "progress",
// and the header marks the chapter as current from "activeFrom".
const CHAPTER_POINTS = {
    profile: { progress: 0.257, activeFrom: 0 },
    maho: { progress: 0.49, activeFrom: 0.28 },
    world: { progress: 0.72, activeFrom: 0.5 },
    story: { progress: 0.97, activeFrom: 0.735 }
}

const TIMELINE_LENGTH = 14

const CHAPTER_LABELS = ['PROFILE 1', 'PROFILE 2', 'WORLD', 'STORY']

const PHOTO_ROTATIONS = [-6, 4, 3, -4]

const PHOTO_POSITIONS = [
    'left-[3vw] top-[14svh] lg:left-[2vw] lg:top-[24vh]',
    'right-[3vw] top-[11svh] lg:left-[27vw] lg:right-auto lg:top-[12vh]',
    'left-[5vw] bottom-[24svh] lg:left-[4vw] lg:bottom-[24vh]',
    'right-[4vw] bottom-[20svh] lg:left-[30vw] lg:right-auto lg:bottom-[20vh]'
]

const PLANTS = [
    { variant: 'flower' as const, seed: 21, className: 'left-[60%] bottom-[4svh] w-[24vw] lg:left-[34vw] lg:bottom-[4vh] lg:w-[8vw]', mirror: false, duration: '6.8s' },
    { variant: 'branch' as const, seed: 5, className: 'left-[-12vw] bottom-[-5svh] w-[40vw] lg:left-[-3vw] lg:bottom-[-4vh] lg:w-[17vw]', mirror: false, duration: '8s' },
    { variant: 'branch' as const, seed: 17, className: 'right-[-14vw] bottom-[-6svh] w-[38vw] lg:right-[-3vw] lg:bottom-[-6vh] lg:w-[12vw]', mirror: true, duration: '9s' }
]

// On desktop every chapter text sits in one column on the right, below the fixed header and the
// YouTube card (top 12rem), and is centred in the space that is left, so no chapter ever runs
// under the header. Phones get one card per chapter scrolling over the pinned backdrop.
const ARTICLE_CLASS = cn(
    'relative mx-auto my-[26svh] w-[min(92vw,560px)] rounded-2xl bg-paper/90 p-6 ring-1 ring-ink/10 backdrop-blur-md sm:p-9',
    'group-data-[enhanced]:absolute group-data-[enhanced]:bottom-[5vh] group-data-[enhanced]:right-[6vw] group-data-[enhanced]:top-[12rem] group-data-[enhanced]:m-0',
    'group-data-[enhanced]:flex group-data-[enhanced]:w-[min(520px,37vw)] group-data-[enhanced]:flex-col group-data-[enhanced]:justify-center',
    'group-data-[enhanced]:rounded-none group-data-[enhanced]:bg-transparent group-data-[enhanced]:p-0 group-data-[enhanced]:ring-0 group-data-[enhanced]:backdrop-blur-none'
)

// Panels after the first stay hidden on desktop until the timeline reveals them, even before the
// animation has measured anything. Only the visible panel takes clicks.
const PANEL_CLASS = 'flex flex-col gap-5 group-data-[enhanced]:pointer-events-auto lg:gap-[clamp(10px,1.9vh,20px)]'
const LATER_PANEL_CLASS = 'group-data-[enhanced]:invisible group-data-[enhanced]:opacity-0'

const PANEL_TITLE_CLASS = 'text-[clamp(3rem,10vw,5rem)] lg:text-[clamp(2.5rem,min(6vw,9.5vh),5.5rem)]'
const BODY_CLASS = 'text-[15px] leading-[2.05] tracking-[0.05em] text-ink/85 lg:text-[clamp(13px,1.7vh,15px)] lg:leading-[2]'
const LEAD_CLASS = 'text-lg leading-[1.85] tracking-[0.06em] lg:text-[clamp(15px,2.1vh,19px)]'
const CODE_CLASS = 'flex items-center gap-1.5 font-mono text-[10px] tracking-[0.2em] text-ink-soft'

const pad = (value: number): string => String(value).padStart(2, '0')

interface ProfileDataProps {
    rows: Array<ProfileRow>
}

const ProfileData: FC<ProfileDataProps> = ({ rows }): ReactNode => (
    <dl className='grid grid-cols-1 border-t border-ink/15 sm:grid-cols-2 sm:gap-x-6'>
        {rows.map(row => (
            <div
                key={row.label}
                data-ch-item
                className={cn('flex items-baseline justify-between gap-4 border-b border-ink/15 py-2.5 lg:py-[clamp(6px,1.1vh,10px)]', row.wide && 'sm:col-span-2')}
            >
                <dt className='shrink-0 font-mono text-[10px] tracking-[0.22em] text-ink-soft'>
                    {row.label}
                    <span className='ml-2 font-sans text-[11px] tracking-[0.08em]'>{row.ja}</span>
                </dt>
                <dd className='text-right text-sm tracking-[0.05em]'>{row.value}</dd>
            </div>
        ))}
    </dl>
)

interface DormitoryCardProps {
    className?: string
}

// 局員寮: a small point on the survey map that leads to the FANBOX support plans.
const DormitoryCard: FC<DormitoryCardProps> = ({ className }): ReactNode => {
    const dorm = WORLD_COPY.dormitory

    return (
        <div className={cn('flex flex-col gap-2 rounded-xl bg-paper/85 p-4 ring-1 ring-gold/35 backdrop-blur-sm', className)}>
            <p className={CODE_CLASS}>
                <Sparkle className='size-2.5 text-gold' />
                {dorm.code}
            </p>
            <p className='text-base tracking-[0.12em]'>{dorm.name}</p>
            <p className='text-xs leading-[1.8] tracking-[0.05em] text-ink/75'>{dorm.text}</p>
            <a
                href={dorm.url}
                target='_blank'
                rel='noopener noreferrer'
                className='mt-1 inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1.5 text-xs tracking-[0.12em] ring-1 ring-ink/20 transition-[background-color,box-shadow,color] duration-500 hover:bg-gold/10 hover:text-ink hover:ring-gold/70 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
            >
                {dorm.cta}
                <ArrowUpRight className='size-3.5' />
                <span className='sr-only'>（FANBOX が新しいタブで開きます）</span>
            </a>
        </div>
    )
}

// PROFILE 1 (Nia), PROFILE 2 (MAHO), WORLD and STORY as one pinned scene. On desktop Nia stays on
// screen while the chapters switch and MAHO, the observation UI, the map, record cards and plants
// are added. On phones the same scene is a pinned backdrop with the chapters scrolling over it.
const ChaptersSection: FC = (): ReactNode => {
    const sectionRef = useRef<HTMLElement>(null)
    const [variant, setVariant] = useState(0)

    const showVariant = (index: number): void => {
        setVariant((index + NIA_VARIANTS.length) % NIA_VARIANTS.length)
    }

    useGSAP(() => {
        const section = sectionRef.current

        if (!section) return

        const q = gsap.utils.selector(section)
        const mm = gsap.matchMedia()

        const flashIn = (): void => {
            gsap.fromTo(q('[data-ch="flash"]'), { autoAlpha: 1 }, {
                autoAlpha: 0,
                ease: 'power1.out',
                scrollTrigger: { trigger: section, start: 'top bottom', end: 'top top', scrub: true }
            })
        }

        mm.add(MOTION_QUERIES.desktop, () => {
            section.setAttribute('data-enhanced', '')

            flashIn()

            const panel = (name: string): HTMLElement => q(`[data-ch-panel="${name}"]`)[0]
            const chars = (name: string) => q(`[data-ch-panel="${name}"] [data-char]`)
            const meta = (name: string) => q(`[data-ch-panel="${name}"] [data-heading-meta]`)
            const items = (name: string) => q(`[data-ch-panel="${name}"] [data-ch-item]`)

            const character = q('[data-ch="character"]')
            const glow = q('[data-ch="glow"]')
            const maho = q('[data-ch="maho"]')
            const mahoGlow = q('[data-ch="maho-glow"]')
            const shift = q('[data-ch-shift]')
            const windowView = q('[data-ch="window"]')
            const photos = q('[data-ch="photo"]')
            const plants = q('[data-ch="plant"]')

            const lat = q('[data-ch-lat]')[0]
            const lon = q('[data-ch-lon]')[0]
            const alt = q('[data-ch-alt]')[0]
            const label = q('[data-ch-chapter-label]')[0]
            const bars = q('[data-ch-chapter]')
            const starts = [CHAPTER_POINTS.maho.activeFrom, CHAPTER_POINTS.world.activeFrom, CHAPTER_POINTS.story.activeFrom]

            let chapter = -1

            const updateHud = (progress: number): void => {
                if (lat) lat.textContent = (23.4721 + progress * 0.0846).toFixed(4)
                if (lon) lon.textContent = (120.9518 - progress * 0.0613).toFixed(4)
                if (alt) alt.textContent = String(Math.round(412 + progress * 286)).padStart(4, '0')

                const next = starts.filter(start => progress >= start).length

                if (next === chapter) return

                chapter = next
                bars.forEach((bar, index) => bar.toggleAttribute('data-current', index === next))

                if (label) label.textContent = `CHAPTER ${pad(next + 1)} — ${CHAPTER_LABELS[next]}`
            }

            gsap.set(character, { transformOrigin: '50% 100%' })
            gsap.set([...maho, ...plants], { transformOrigin: '50% 100%' })
            gsap.set(q('[data-ch="hud"] [data-hud-ring="outer"]'), { svgOrigin: '300 300' })

            // MAHO hovers gently on its own (a separate transform part from the scroll movement).
            gsap.to(maho, { yPercent: -4, duration: 2.6, ease: 'sine.inOut', repeat: -1, yoyo: true })

            const tl = gsap.timeline({
                defaults: { ease: 'power2.out', duration: 0.6 },
                scrollTrigger: {
                    trigger: section,
                    start: 'top top',
                    end: 'bottom bottom',
                    scrub: 1,
                    invalidateOnRefresh: true,
                    onUpdate: self => updateHud(self.progress),
                    onRefresh: primeOnRefresh
                }
            })

            // Every letter and line gets its own tween (never `stagger`), so a refresh puts all of
            // them back into their hidden starting state. Later panels also fade in as a whole.
            const reveal = (name: string, at: number, itemsAt: number, step: number, fadePanel: boolean): void => {
                if (fadePanel) tl.fromTo(panel(name), { autoAlpha: 0, y: 0 }, { autoAlpha: 1, y: 0, duration: 0.25, ease: 'none' }, at - 0.15)

                fromToEach(tl, meta(name), { autoAlpha: 0, x: -18 }, { autoAlpha: 1, x: 0, duration: 0.5 }, at - 0.05, 0.12)
                fromToEach(tl, chars(name), { autoAlpha: 0, y: 34, filter: 'blur(8px)' }, { autoAlpha: 1, y: 0, filter: 'blur(0px)', duration: 0.6 }, at, 0.06)
                fromToEach(tl, items(name), { autoAlpha: 0, y: 28 }, { autoAlpha: 1, y: 0, duration: 0.55 }, itemsAt, step)
            }

            // Nia appears, the observation rings wake up
            tl.fromTo(character, { autoAlpha: 0, y: () => window.innerHeight * 0.1, scale: 1.06 }, { autoAlpha: 1, y: 0, scale: 1, duration: 1 }, 0)
                .fromTo(glow, { autoAlpha: 0, scale: 0.85 }, { autoAlpha: 1, scale: 1, duration: 1 }, 0)
                .fromTo(q('[data-ch="hud"]'), { autoAlpha: 0, scale: 0.88 }, { autoAlpha: 1, scale: 1, duration: 1.2 }, 0.15)
                .fromTo(q('[data-ch="hud"] [data-hud-ring="outer"]'), { rotation: 0 }, { rotation: 170, duration: TIMELINE_LENGTH, ease: 'none' }, 0)

            // PROFILE 1: Nia
            reveal('profile', 0.5, 1.2, 0.22, false)

            tl.fromTo(plants[0], { autoAlpha: 0, scale: 0.25 }, { autoAlpha: 1, scale: 1, duration: 1.1, ease: 'power3.out' }, 1.8)

            // PROFILE 2: Nia steps aside and MAHO floats in beside her
            tl.to(panel('profile'), { autoAlpha: 0, y: -40, duration: 0.6 }, 3.9)
                .to(character, { x: () => -window.innerWidth * 0.05, scale: 0.93, duration: 1 }, 3.9)
                .fromTo(mahoGlow, { autoAlpha: 0, scale: 0.7 }, { autoAlpha: 1, scale: 1, duration: 1 }, 4)
                .fromTo(maho, { autoAlpha: 0, x: () => window.innerWidth * 0.08, y: () => window.innerHeight * 0.06, scale: 0.88 }, { autoAlpha: 1, x: 0, y: 0, scale: 1, duration: 1.1 }, 4)

            reveal('maho', 4.4, 4.9, 0.22, true)

            // WORLD: the scene glides sideways, both fade, the observation window slides over
            // and the survey map draws itself
            tl.to(panel('maho'), { autoAlpha: 0, y: -40, duration: 0.6 }, 7)
                .to([...maho, ...mahoGlow], { autoAlpha: 0, duration: 0.7 }, 7)
                .to(maho, { x: () => window.innerWidth * 0.04, duration: 0.8 }, 7)
                .to(shift, { x: () => -window.innerWidth * 0.04, duration: 1.2, ease: 'power1.inOut' }, 7)
                .to(character, { autoAlpha: 0, x: () => -window.innerWidth * 0.08, duration: 0.8 }, 7)
                .to(glow, { autoAlpha: 0, duration: 0.8 }, 7)
                .fromTo(windowView, { autoAlpha: 0, x: () => window.innerWidth * 0.14 }, { autoAlpha: 1, x: 0, duration: 1.1 }, 7.15)
                .fromTo(q('[data-ch="window"] [data-map-grid]'), { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.6 }, 7.5)

            fromToEach(tl, q('[data-ch="window"] [data-map-draw]'), { strokeDasharray: 1, strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 1.4, ease: 'none' }, 7.6, 0.06)
            fromToEach(tl, q('[data-ch="window"] [data-map-marker]'), { autoAlpha: 0, scale: 0.6, transformOrigin: '50% 50%' }, { autoAlpha: 1, scale: 1, duration: 0.5 }, 8.6, 0.18)

            tl.fromTo(q('[data-ch="dorm"]'), { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.5 }, 9.3)

            reveal('world', 7.8, 8.3, 0.3, true)

            tl.fromTo(plants[1], { autoAlpha: 0, scale: 0.25 }, { autoAlpha: 1, scale: 1, duration: 1.2, ease: 'power3.out' }, 8.4)

            // STORY: the window fades, Nia returns and old record cards pile up around her
            tl.to(panel('world'), { autoAlpha: 0, y: -40, duration: 0.6 }, 10.3)
                .to(windowView, { autoAlpha: 0, scale: 0.94, duration: 0.8 }, 10.3)
                .to(shift, { x: () => -window.innerWidth * 0.07, duration: 1.2, ease: 'power1.inOut' }, 10.3)
                .to(character, { autoAlpha: 1, x: () => -window.innerWidth * 0.015, scale: 0.92, duration: 1 }, 10.55)
                .to(glow, { autoAlpha: 1, duration: 1 }, 10.55)

            photos.forEach((photo, index) => {
                tl.fromTo(
                    photo,
                    { autoAlpha: 0, y: () => window.innerHeight * 0.18, rotate: PHOTO_ROTATIONS[index] * 2.6 },
                    { autoAlpha: 1, y: 0, rotate: PHOTO_ROTATIONS[index], duration: 0.9, ease: 'power3.out' },
                    10.6 + index * 0.4
                )
            })

            reveal('story', 11, 11.5, 0.3, true)

            tl.fromTo(plants[2], { autoAlpha: 0, scale: 0.25 }, { autoAlpha: 1, scale: 1, duration: 1.2, ease: 'power3.out' }, 11.8)
                .to({}, { duration: 0.01 }, TIMELINE_LENGTH)

            updateHud(0)

            return () => section.removeAttribute('data-enhanced')
        })

        mm.add(MOTION_QUERIES.mobile, () => {
            flashIn()

            const character = q('[data-ch="character"]')
            const windowView = q('[data-ch="window"]')
            const photos = q('[data-ch="photo"]')
            const plants = q('[data-ch="plant"]')

            gsap.set(plants, { transformOrigin: '50% 100%' })

            gsap.fromTo(character, { autoAlpha: 0, y: 60 }, {
                autoAlpha: 1,
                y: 0,
                ease: 'power2.out',
                scrollTrigger: { trigger: section, start: 'top 70%', end: 'top top', scrub: true }
            })

            gsap.fromTo(plants, { autoAlpha: 0, scale: 0.3 }, {
                autoAlpha: 1,
                scale: 1,
                stagger: 0.15,
                ease: 'power2.out',
                scrollTrigger: { trigger: section, start: 'top 40%', end: 'top -40%', scrub: true }
            })

            q('[data-ch-panel]').forEach(panelElement => {
                gsap.timeline({
                    scrollTrigger: { trigger: panelElement, start: 'top 80%', toggleActions: 'play none none reverse' }
                })
                    .from(panelElement.querySelectorAll('[data-heading-meta]'), { autoAlpha: 0, x: -12, duration: 0.8, stagger: 0.1 })
                    .from(panelElement.querySelectorAll('[data-char]'), { autoAlpha: 0, y: 24, duration: 0.9, stagger: 0.05, ease: 'power3.out' }, 0)

                panelElement.querySelectorAll('[data-ch-item]').forEach(item => {
                    gsap.from(item, {
                        autoAlpha: 0,
                        y: 24,
                        duration: 1,
                        ease: 'power2.out',
                        scrollTrigger: { trigger: item, start: 'top 90%', toggleActions: 'play none none reverse' }
                    })
                })
            })

            const world = q('[data-ch-root="world"]')[0]
            const story = q('[data-ch-root="story"]')[0]

            if (world) {
                gsap.timeline({ scrollTrigger: { trigger: world, start: 'top 90%', end: 'top 40%', scrub: true } })
                    .fromTo(character, { autoAlpha: 1 }, { autoAlpha: 0.15, duration: 1, immediateRender: false }, 0)
                    .fromTo(windowView, { autoAlpha: 0, y: 40 }, { autoAlpha: 1, y: 0, duration: 1 }, 0)
                    .fromTo(q('[data-ch="window"] [data-map-draw]'), { strokeDasharray: 1, strokeDashoffset: 1 }, { strokeDashoffset: 0, duration: 1, stagger: 0.03 }, 0.2)
            }

            if (story) {
                gsap.timeline({ scrollTrigger: { trigger: story, start: 'top 90%', end: 'top 40%', scrub: true } })
                    .fromTo(windowView, { autoAlpha: 1 }, { autoAlpha: 0, duration: 1, immediateRender: false }, 0)
                    .fromTo(character, { autoAlpha: 0.15 }, { autoAlpha: 1, duration: 1, immediateRender: false }, 0)
                    .fromTo(photos, { autoAlpha: 0, y: 50 }, { autoAlpha: 1, y: 0, stagger: 0.15, duration: 0.8 }, 0.2)
            }
        })
    }, { scope: sectionRef })

    const current = NIA_VARIANTS[variant] ?? NIA_VARIANTS[0]

    return (
        <section
            id='chapters'
            ref={sectionRef}
            data-scroll-track
            aria-label='プロフィールと世界観'
            className='group relative bg-paper data-[enhanced]:h-[820vh]'
        >
            <div className='sticky top-0 h-svh overflow-hidden'>
                <div className='absolute inset-0 bg-[radial-gradient(120%_85%_at_28%_42%,#fdfcf8_0%,#f5f2ea_55%,#ebe5d6_100%)]' />
                <div className='absolute inset-0 bg-[linear-gradient(to_right,rgba(29,42,51,0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgba(29,42,51,0.045)_1px,transparent_1px)] bg-[size:88px_88px]' />

                <div data-ch='scene' data-ch-shift className='absolute inset-0'>
                    <div className='pointer-events-none absolute inset-x-0 top-[9svh] flex justify-center group-data-[enhanced]:top-[5vh] group-data-[enhanced]:justify-start group-data-[enhanced]:pl-[3vw]'>
                        <div data-ch='hud' className='w-[92vw] max-w-[640px] shrink-0 text-ink/70 group-data-[enhanced]:w-[min(84vh,46vw)] group-data-[enhanced]:max-w-none'>
                            <ObservationHud className='h-auto w-full' />
                        </div>
                    </div>

                    <div
                        data-ch='window'
                        className='invisible absolute inset-x-4 top-[17svh] aspect-[4/3] opacity-0 group-data-[enhanced]:inset-x-auto group-data-[enhanced]:left-[4vw] group-data-[enhanced]:top-[13vh] group-data-[enhanced]:aspect-auto group-data-[enhanced]:h-[62vh] group-data-[enhanced]:w-[50vw]'
                    >
                        <div className='relative h-full w-full overflow-hidden rounded-[18px] bg-paper shadow-[0_30px_80px_-30px_rgba(29,42,51,0.45)] ring-1 ring-ink/15'>
                            <div className='absolute inset-0 bg-[linear-gradient(160deg,#e2eff7_0%,#f5f2ea_55%,#fbfaf6_100%)]' />
                            <div className='absolute inset-0 bg-[linear-gradient(to_right,rgba(29,42,51,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(29,42,51,0.05)_1px,transparent_1px)] bg-[size:28px_28px]' />
                            <WorldMap className='absolute inset-0 h-full w-full p-[4%] text-ink' />

                            <div className='absolute inset-x-4 top-3 flex items-center justify-between font-mono text-[9px] tracking-[0.28em] text-ink/75 sm:text-[10px]'>
                                <span>SURVEY MAP — ELECTRONIC WORLD</span>
                                <span className='flex items-center gap-1.5'>
                                    <span className='size-1.5 rounded-full bg-coral motion-safe:animate-onair' />
                                    REC
                                </span>
                            </div>

                            <div data-ch='dorm' className='absolute right-4 top-10 hidden w-[min(15rem,42%)] group-data-[enhanced]:block'>
                                <DormitoryCard />
                            </div>

                            <span className='absolute left-3 top-3 size-5 border-l border-t border-ink/50' />
                            <span className='absolute right-3 top-3 size-5 border-r border-t border-ink/50' />
                            <span className='absolute bottom-3 left-3 size-5 border-b border-l border-ink/50' />
                            <span className='absolute bottom-3 right-3 size-5 border-b border-r border-ink/50' />
                        </div>
                    </div>

                    {RECORD_PHOTOS.map((photo, index) => (
                        <figure
                            key={photo.code}
                            data-ch='photo'
                            className={cn(
                                'invisible absolute w-[40vw] max-w-[260px] rounded-[3px] bg-[#fbf8f0] p-1.5 pb-7 opacity-0 shadow-[0_18px_40px_-18px_rgba(29,42,51,0.5)] ring-1 ring-ink/10 sm:p-2 sm:pb-9 lg:w-[15vw] lg:max-w-none',
                                PHOTO_POSITIONS[index]
                            )}
                        >
                            <div role='img' aria-label={photo.label} className='relative aspect-[4/3] overflow-hidden bg-ink/5'>
                                <PatternArt variant={photo.art} className='absolute inset-0 h-full w-full' />
                            </div>
                            <figcaption className='absolute inset-x-2 bottom-1.5 flex items-center justify-between gap-2 font-mono text-[8px] tracking-[0.14em] text-ink/70 sm:bottom-3 sm:text-[9px]'>
                                <span className='truncate'>{photo.code} {photo.caption}</span>
                                <span className='shrink-0'>{photo.time}</span>
                            </figcaption>
                            <span aria-hidden='true' className='absolute -top-2 left-1/2 h-4 w-12 -translate-x-1/2 -rotate-3 bg-[#eadfbf]/80' />
                        </figure>
                    ))}
                </div>

                <div data-ch-shift className='pointer-events-none absolute bottom-0 left-[-6%] h-[22svh] w-[116%] lg:h-[30vh]'>
                    <GeoGround className='absolute inset-0 h-full w-full' />
                </div>

                <div data-ch-shift className='pointer-events-none absolute inset-0'>
                    {PLANTS.map((plant, index) => (
                        <div key={plant.seed} data-ch='plant' className={cn('absolute', plant.className)}>
                            <div className='origin-bottom motion-safe:animate-sway' style={{ animationDuration: plant.duration, animationDelay: `${-index * 1.3}s` }}>
                                <MahoniaSprig variant={plant.variant} seed={plant.seed} className={plant.mirror ? '-scale-x-100' : undefined} />
                            </div>
                        </div>
                    ))}
                </div>

                <div className='pointer-events-none absolute inset-x-0 bottom-0 flex justify-center group-data-[enhanced]:justify-start group-data-[enhanced]:pl-[3vw]'>
                    <div className='relative aspect-[3/4] h-[72svh] lg:h-[86svh] group-data-[enhanced]:h-[94svh]'>
                        <div
                            data-ch='glow'
                            className='absolute inset-[-14%] rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,1),rgba(255,255,255,0.94)_48%,rgba(255,255,255,0.45)_72%,rgba(255,255,255,0)_88%)]'
                        />
                        {NIA_VARIANTS.map((item, index) => (
                            <Image
                                key={item.id}
                                data-ch='character'
                                src={item.src}
                                alt={index === variant ? item.alt : ''}
                                aria-hidden={index === variant ? undefined : true}
                                fill
                                sizes='(min-width: 1024px) 46vw, 92vw'
                                className={cn(
                                    'select-none object-contain object-bottom mix-blend-multiply transition-[filter] ease-out will-change-transform',
                                    index === variant ? '[filter:opacity(1)] delay-200 duration-700' : '[filter:opacity(0)] duration-300'
                                )}
                            />
                        ))}
                    </div>
                </div>

                <div className='pointer-events-none absolute bottom-[20vh] left-[31vw] hidden aspect-square w-[min(42vh,23vw)] group-data-[enhanced]:block'>
                    <div
                        data-ch='maho-glow'
                        className='invisible absolute inset-[-18%] rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,1),rgba(255,255,255,0.9)_50%,rgba(255,255,255,0)_86%)] opacity-0'
                    />
                    <Image
                        data-ch='maho'
                        src={MAHO_COPY.image.src}
                        alt={MAHO_COPY.image.alt}
                        fill
                        sizes='24vw'
                        className='invisible select-none object-contain opacity-0 mix-blend-multiply'
                    />
                </div>

                <div className='pointer-events-none absolute left-5 top-20 hidden font-mono text-[10px] tracking-[0.3em] text-ink/70 group-data-[enhanced]:block lg:left-10 lg:top-28'>
                    <p className='flex items-center gap-2'>
                        <span className='size-1.5 rounded-full bg-coral motion-safe:animate-onair' />
                        OBSERVATION LOG
                    </p>
                    <p data-ch-chapter-label className='mt-1.5'>CHAPTER 01 — PROFILE 1</p>
                    <div className='mt-3 flex gap-1.5'>
                        {CHAPTER_LABELS.map((chapterLabel, index) => (
                            <span
                                key={chapterLabel}
                                data-ch-chapter
                                data-current={index === 0 ? '' : undefined}
                                className='h-0.5 w-8 bg-ink/15 transition-colors duration-700 data-[current]:bg-gold'
                            />
                        ))}
                    </div>
                </div>

                <div className='pointer-events-none absolute bottom-6 left-10 hidden font-mono text-[10px] leading-[1.9] tracking-[0.28em] text-ink/70 group-data-[enhanced]:block'>
                    <p>LAT <span data-ch-lat>23.4721</span>°N</p>
                    <p>LON <span data-ch-lon>120.9518</span>°E</p>
                    <p>ALT <span data-ch-alt>0412</span>m</p>
                </div>

                <div data-ch='flash' aria-hidden='true' className='pointer-events-none absolute inset-0 bg-mist opacity-0' />
            </div>

            <div className='relative z-10 pb-[10svh] group-data-[enhanced]:pointer-events-none group-data-[enhanced]:sticky group-data-[enhanced]:top-0 group-data-[enhanced]:-mt-[100svh] group-data-[enhanced]:h-svh group-data-[enhanced]:pb-0'>
                <article
                    id='profile'
                    data-ch-root='profile'
                    data-scroll-progress={CHAPTER_POINTS.profile.progress}
                    data-active-from={CHAPTER_POINTS.profile.activeFrom}
                    className={ARTICLE_CLASS}
                >
                    <div data-ch-panel='profile' className={PANEL_CLASS}>
                        <SectionHeading
                            index={PROFILE_COPY.index}
                            kicker={PROFILE_COPY.kicker}
                            title={PROFILE_COPY.title}
                            ja={PROFILE_COPY.ja}
                            titleClassName={PANEL_TITLE_CLASS}
                        />
                        <p data-ch-item className={LEAD_CLASS}>{PROFILE_COPY.lead}</p>
                        <p data-ch-item className={BODY_CLASS}>{PROFILE_COPY.body}</p>

                        <div data-ch-item className='flex flex-col gap-2'>
                            <p className='flex items-center justify-between font-mono text-[10px] tracking-[0.24em] text-ink-soft'>
                                <span className='flex items-center gap-2'>
                                    <Sparkle className='size-2.5 text-gold' />
                                    STANDING ART
                                </span>
                                <span aria-live='polite'>
                                    {`${pad(variant + 1)} / ${pad(NIA_VARIANTS.length)} ${current.label}`}
                                </span>
                            </p>
                            <div role='group' aria-label='天満ニアの立ち絵の切り替え' className='flex items-center gap-2'>
                                <button
                                    type='button'
                                    onClick={() => showVariant(variant - 1)}
                                    aria-label='前の立ち絵'
                                    className='flex size-9 shrink-0 items-center justify-center rounded-full ring-1 ring-ink/15 transition-colors duration-300 hover:bg-ink/5 focus-visible:outline-2 focus-visible:outline-gold'
                                >
                                    <ChevronLeft className='size-4' />
                                </button>
                                <div className='flex items-start gap-2'>
                                    {NIA_VARIANTS.map((item, index) => (
                                        <button
                                            key={item.id}
                                            type='button'
                                            onClick={() => showVariant(index)}
                                            aria-pressed={index === variant}
                                            aria-label={`${item.label}の立ち絵を表示`}
                                            className='group/thumb flex flex-col items-center gap-1 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold'
                                        >
                                            <span
                                                className={cn(
                                                    'relative block h-12 w-9 overflow-hidden rounded-lg bg-mist ring-1 transition-[box-shadow,opacity] duration-300',
                                                    index === variant ? 'ring-2 ring-gold' : 'opacity-60 ring-ink/15 group-hover/thumb:opacity-100'
                                                )}
                                            >
                                                <Image src={item.src} alt='' fill sizes='36px' className='object-cover object-top mix-blend-multiply' />
                                            </span>
                                            <span className={cn('text-[10px] tracking-[0.1em]', index === variant ? 'text-ink' : 'text-ink-soft')}>
                                                {item.label}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                <button
                                    type='button'
                                    onClick={() => showVariant(variant + 1)}
                                    aria-label='次の立ち絵'
                                    className='flex size-9 shrink-0 items-center justify-center rounded-full ring-1 ring-ink/15 transition-colors duration-300 hover:bg-ink/5 focus-visible:outline-2 focus-visible:outline-gold'
                                >
                                    <ChevronRight className='size-4' />
                                </button>
                            </div>
                        </div>

                        <ProfileData rows={PROFILE_COPY.data} />
                    </div>
                </article>

                <article
                    id='maho'
                    data-ch-root='maho'
                    data-scroll-progress={CHAPTER_POINTS.maho.progress}
                    data-active-from={CHAPTER_POINTS.maho.activeFrom}
                    className={ARTICLE_CLASS}
                >
                    <div data-ch-panel='maho' className={cn(PANEL_CLASS, LATER_PANEL_CLASS)}>
                        <SectionHeading
                            index={MAHO_COPY.index}
                            kicker={MAHO_COPY.kicker}
                            title={MAHO_COPY.title}
                            ja={MAHO_COPY.ja}
                            titleClassName={PANEL_TITLE_CLASS}
                        />
                        <div data-ch-item className='relative mx-auto aspect-square w-[min(64vw,260px)] group-data-[enhanced]:hidden'>
                            <span aria-hidden='true' className='absolute inset-[-10%] rounded-full bg-[radial-gradient(closest-side,rgba(255,255,255,0.95),rgba(255,255,255,0))]' />
                            <Image
                                src={MAHO_COPY.image.src}
                                alt={MAHO_COPY.image.alt}
                                fill
                                sizes='260px'
                                className='object-contain mix-blend-multiply'
                            />
                        </div>
                        <p data-ch-item className={LEAD_CLASS}>{MAHO_COPY.lead}</p>
                        <p data-ch-item className={BODY_CLASS}>{MAHO_COPY.body}</p>
                        <ProfileData rows={MAHO_COPY.data} />
                    </div>
                </article>

                <article
                    id='world'
                    data-ch-root='world'
                    data-scroll-progress={CHAPTER_POINTS.world.progress}
                    data-active-from={CHAPTER_POINTS.world.activeFrom}
                    className={ARTICLE_CLASS}
                >
                    <div data-ch-panel='world' className={cn(PANEL_CLASS, LATER_PANEL_CLASS)}>
                        <SectionHeading
                            index={WORLD_COPY.index}
                            kicker={WORLD_COPY.kicker}
                            title={WORLD_COPY.title}
                            ja={WORLD_COPY.ja}
                            titleClassName={PANEL_TITLE_CLASS}
                        />
                        <p data-ch-item className={LEAD_CLASS}>{WORLD_COPY.lead}</p>
                        <p data-ch-item className={BODY_CLASS}>{WORLD_COPY.body}</p>

                        <ul data-ch-item aria-label='マホニア観測局のメンバー' className='grid grid-cols-3 gap-3 border-y border-ink/15 py-3'>
                            {WORLD_COPY.members.map(member => (
                                <li key={member.code} className='flex flex-col gap-1'>
                                    <span className='font-mono text-[9px] tracking-[0.2em] text-ink-soft'>{member.code}</span>
                                    <span className='text-[11px] tracking-[0.1em] text-ink-soft'>{member.ja}</span>
                                    <span className='text-sm tracking-[0.06em]'>{member.name}</span>
                                </li>
                            ))}
                        </ul>

                        <ul data-ch-item aria-label='観測局の活動' className='grid grid-cols-2 gap-x-5 gap-y-3'>
                            {WORLD_COPY.activities.map(activity => (
                                <li key={activity.code} className='flex flex-col gap-0.5'>
                                    <span className={CODE_CLASS}>
                                        <Sparkle className='size-2.5 text-gold' />
                                        {activity.code}
                                    </span>
                                    <span className='text-[13px] tracking-[0.06em]'>
                                        <span className='text-ink-soft'>{activity.ja}</span>
                                        <span aria-hidden='true' className='mx-1.5 text-ink/30'>/</span>
                                        {activity.text}
                                    </span>
                                </li>
                            ))}
                        </ul>

                        <div data-ch-item className='group-data-[enhanced]:hidden'>
                            <DormitoryCard />
                        </div>
                    </div>
                </article>

                <article
                    id='story'
                    data-ch-root='story'
                    data-scroll-progress={CHAPTER_POINTS.story.progress}
                    data-active-from={CHAPTER_POINTS.story.activeFrom}
                    className={ARTICLE_CLASS}
                >
                    <div data-ch-panel='story' className={cn(PANEL_CLASS, LATER_PANEL_CLASS)}>
                        <SectionHeading
                            index={STORY_COPY.index}
                            kicker={STORY_COPY.kicker}
                            title={STORY_COPY.title}
                            ja={STORY_COPY.ja}
                            titleClassName={PANEL_TITLE_CLASS}
                        />
                        <p data-ch-item className='text-xl tracking-[0.12em] lg:text-[clamp(17px,2.4vh,22px)]'>{STORY_COPY.quote}</p>
                        {STORY_COPY.paragraphs.map(text => (
                            <p key={text} data-ch-item className={BODY_CLASS}>{text}</p>
                        ))}

                        <div data-ch-item className='flex flex-col gap-1.5 border-l-2 border-gold/50 bg-ink/[0.03] px-4 py-3 font-mono text-[10px] leading-[1.8] tracking-[0.12em] text-ink/75'>
                            <p className='flex items-center justify-between gap-3 text-ink-soft'>
                                <span>{STORY_COPY.archive.label}</span>
                                <span>{STORY_COPY.archive.fragment}</span>
                            </p>
                            <p className='text-[11px] text-ink'>{STORY_COPY.archive.name}</p>
                            {STORY_COPY.archive.lines.map(line => (
                                <p key={line} className='font-sans text-xs tracking-[0.06em]'>{line}</p>
                            ))}
                            <p className='flex flex-wrap items-center gap-x-2 font-sans text-xs tracking-[0.06em]'>
                                <span>{STORY_COPY.archive.missing}</span>
                                <span aria-hidden='true' className='inline-block h-2.5 w-20 bg-ink/15' />
                                <span className='font-mono text-[10px] tracking-[0.18em] text-coral'>{STORY_COPY.archive.missingValue}</span>
                            </p>
                        </div>
                    </div>
                </article>
            </div>
        </section>
    )
}

export default ChaptersSection
